import { useCallback, useEffect, useState } from 'react';
import { useLlcApi } from '../Api';
import { PagedResult } from '../types/api';
import { ActiveServiceRequestSummaryDto } from '../types/serviceRequests';

// A single user is not expected to exceed this; pagination UI can be added later if needed.
const PAGE_SIZE = 100;

// Service-type name copied from the LLC app's src/frontend/src/types/index.ts. Keep in sync.
const REGISTERED_AGENT_SERVICE_TYPE = 'Registered Agent';

interface ServiceRequestsState {
  requests: ActiveServiceRequestSummaryDto[];
  loading: boolean;
  error: boolean;
}

/**
 * Fetches the logged-in user's active service requests from the LLC API as a single flat list
 * (the dashboard's "Active Requests" section). Completed requests are excluded so the section
 * matches its title (and the LLC app's own default of hiding completed work).
 */
export function useServiceRequests(): ServiceRequestsState {
  const llcApi = useLlcApi();
  const [state, setState] = useState<ServiceRequestsState>({
    requests: [],
    loading: true,
    error: false,
  });

  const load = useCallback(
    async (signal: AbortSignal) => {
      setState((s) => ({ ...s, loading: true, error: false }));
      try {
        const response = await llcApi.get<
          PagedResult<ActiveServiceRequestSummaryDto>
        >('/api/BusinessEntity/active-service-requests', {
          params: {
            pageNumber: 1,
            pageSize: PAGE_SIZE,
            includeCompleted: false,
          },
          signal,
        });

        if (response.data?.hasNextPage) {
          // No pagination UI yet (see PAGE_SIZE); surface that the list is truncated.
          console.warn(
            `Active service requests truncated to ${PAGE_SIZE}; more pages exist.`
          );
        }

        setState({
          // Registered Agent requests are excluded from the dashboard's Active
          // Requests list: RA has no client-facing detail page in the LLC app,
          // so a row for one has no meaningful destination to link to (it would
          // either be a dead, non-navigable row or bounce the user to the app
          // root with no context). RA provisioning is managed by ops elsewhere,
          // so surfacing it here only adds noise without an actionable action.
          requests: (response.data?.items ?? []).filter(
            (req) => req.serviceTypeName !== REGISTERED_AGENT_SERVICE_TYPE
          ),
          loading: false,
          error: false,
        });
      } catch {
        // Ignore aborts (unmount / effect re-run); only surface real failures.
        if (signal.aborted) return;
        // This catch is the only handler for 5xx and network errors: the Api interceptor
        // redirects on 401 only and rejects everything else for the caller to show inline.
        setState({ requests: [], loading: false, error: true });
      }
    },
    [llcApi]
  );

  useEffect(() => {
    const controller = new AbortController();
    load(controller.signal);
    return () => controller.abort();
  }, [load]);

  return state;
}
